import { createContext, useContext, useMemo, useRef, useState } from "react";

import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";

import useLanguageConverter from "@/shared/hooks/useLanguageConverter";
import { useLoginDataStore } from "@/stores/useLoginDataStore";
import {
  resetAllTablesExceptPaginationAndColumns,
  resetAllTableStores
} from "@/stores/useTableStore";
import { useTokenStore } from "@/stores/useTokenStore";
import { useUserDataStore } from "@/stores/useUserDataStore";

import useAxiosPrivate from "../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../api/resources";
import { RESPONSE_MESSAGES } from "../../api/types";
import { Cookies } from "../../components/Privacy/types";
import { AUTHENTICATION_TYPES, UserDto } from "../../Pages/Auth/types";
import { USER_PERMISSIONS } from "../../roles/types/enums";
import { COOKIE_NAMES, LANGUAGES } from "../../shared";
import useResponse from "../../shared/hooks/useResponse";
import { ALERT_STATUS } from "../alert/types";
import { TimeZoneContext } from "../TimeZone/TimeZoneProvider";

export const AuthContext = createContext<any>({});

interface IAuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: IAuthProviderProps) => {
  const { changeTimeZone, getSystemTimezone } = useContext(TimeZoneContext);

  const { handleCommonError, handleResponse } = useResponse();
  const { getData, patchData } = useAxiosPrivate();
  const { t } = useTranslation();
  const { convertToLocale, handleLanguageChange, convertFromLocale } =
    useLanguageConverter();

  const [cookies] = useCookies([COOKIE_NAMES.Analytics]);

  const { tokenInfo, setTokenInfo } = useTokenStore();

  const isLoginState = useLoginDataStore(state => state.isLoginState);
  const setIsLoginState = useLoginDataStore(state => state.setIsLoginState);
  const is2FaVerification = useLoginDataStore(state => state.is2FaVerification);
  const setIs2FaVerification = useLoginDataStore(
    state => state.setIs2FaVerification
  );

  const isUpdatedLanguage = useLoginDataStore(state => state.isUpdatedLanguage);
  const setIsUpdatedLanguage = useLoginDataStore(
    state => state.setIsUpdatedLanguage
  );

  const user = useUserDataStore(state => state.userData);
  const setUser = useUserDataStore(state => state.setUserData);

  const userSettings = useUserDataStore(state => state.userSettings);
  const setUserSettings = useUserDataStore(state => state.setUserSettings);

  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessageFromServer, setErrorMessageFromServer] =
    useState<boolean>(false);
  const [cookieConsentStatus, setCookieConsentStatus] = useState<Cookies>({
    [COOKIE_NAMES.Analytics]: String(cookies.analytics)
  });
  const [twoFaMethod, setTwoFaMethod] = useState<AUTHENTICATION_TYPES | null>(
    null
  );

  const initialized = useRef(false);

  const jwtToken = tokenInfo?.token;

  const updateLanguageFromLocal = async (localLang: LANGUAGES) => {
    try {
      const convertedLanguage = convertFromLocale(localLang);
      await patchData(ApiResources.UsersMe, {
        language: convertedLanguage
      });
    } catch (error) {
      handleCommonError(error, t);
    }
  };

  const getUserData = async (getTimezone?: boolean) => {
    if (!initialized.current && jwtToken) {
      initialized.current = true;
      const controller = new AbortController();

      // display auth loader only if user is in login state
      isLoginState && setIsAuthLoading(true);

      await getData(ApiResources.UsersMe)
        .then((response: any) => {
          const user: UserDto = response?.data;
          setUser(user);
          setIsAuthLoading(false);

          return user;
        })
        .catch(error => {
          const errorMessage =
            error.message === RESPONSE_MESSAGES.Status401
              ? t("General##unauthorized")
              : error.message;
          handleResponse(ALERT_STATUS.Critical, errorMessage);
        })
        .then((userData: UserDto | void) => {
          if (userData && getTimezone) {
            const timeZoneValue = userData?.timeSettings.timeZone
              ? userData?.timeSettings.timeZone
              : getSystemTimezone();
            changeTimeZone(timeZoneValue);

            if (isUpdatedLanguage) {
              updateLanguageFromLocal(isUpdatedLanguage);
            } else if (userData?.language) {
              const convertedLanguage = convertToLocale(userData?.language);
              handleLanguageChange(convertedLanguage);
            }
          }

          setIsUpdatedLanguage(null);
          setIsAuthLoading(false);
          setIsLoginState(false);
          return userData;
        });

      initialized.current = false;
      controller.abort();
    }
  };

  const toggleTwoFaEnable = (value: boolean) => {
    if (user) {
      setUser({ ...user, twoFactorEnabled: value });
    } else {
      return null;
    }
  };

  const toggle2FaVerification = (value: boolean) => {
    setIs2FaVerification(value);
  };

  const getUserSettings = async () => {
    try {
      const { data } = await getData(ApiResources.UsersSettings);
      setUserSettings(data);

      return data;
    } catch (error) {
      handleCommonError(error, t);
    }
  };

  const getAccessStatus = (feature: USER_PERMISSIONS) =>
    user?.permissions.includes(feature);

  const changeCookieConsentStatus = (
    cookieName: COOKIE_NAMES,
    cookieValue: boolean
  ) => {
    const yearFromNowToExpire = new Date(
      new Date().setFullYear(new Date().getFullYear() + 1)
    );

    const allCookieNames = Object.keys(cookieConsentStatus) as COOKIE_NAMES[];

    const generateCookie = (name: COOKIE_NAMES) =>
      `${name}=${cookieValue}; expires=${yearFromNowToExpire}`;

    const updateCookies = () => {
      if (cookieName === COOKIE_NAMES.All) {
        const allCookiesToSet: {
          [key in COOKIE_NAMES]?: string;
        } = {};

        for (const nameOfCookie of allCookieNames) {
          document.cookie = generateCookie(nameOfCookie);
          allCookiesToSet[nameOfCookie] = String(cookieValue);
        }

        setCookieConsentStatus(prevState => ({
          ...prevState,
          ...allCookiesToSet
        }));
      } else {
        document.cookie = generateCookie(cookieName);

        setCookieConsentStatus(prevState => ({
          ...prevState,
          [cookieName]: String(cookieValue)
        }));
      }
    };

    updateCookies();
  };

  const logoutUser = () => {
    setIsLoading(true);
    setTokenInfo(null);
    setUser(null);

    resetAllTablesExceptPaginationAndColumns();
    setIsLoading(false);
    setIs2FaVerification(false);
    resetAllTableStores();
  };

  // Memoized value of the authentication context
  const contextValue = useMemo(
    () => ({
      cookieConsentStatus,
      isAuthLoading,
      setIsAuthLoading,
      isLoading,
      setIsLoading,
      user,
      userSettings,
      setUser,
      getAccessStatus,
      getUserData,
      getUserSettings,
      tokenInfo,
      setTokenInfo,
      errorMessageFromServer,
      setErrorMessageFromServer,
      toggleTwoFaEnable,
      toggle2FaVerification,
      is2FaVerification,
      changeCookieConsentStatus,
      logoutUser,
      setTwoFaMethod,
      twoFaMethod
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthLoading, isLoading, user, userSettings, getUserSettings, tokenInfo]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthProvider;
